import { useState, useEffect, useMemo } from 'react';
import { BookOpen, Clock, Sparkles, Zap, Shield, Target, ChevronDown, ChevronUp, Sword, Gem, ScrollText, Layers, Flame, Droplets, Crown, Compass, Crosshair, Heart, Swords, Brain, Wand2, Check, Circle, ArrowRight, Award } from 'lucide-react';
import { InkDot } from '@/components/InkDot';
import type { InkColor } from '@/lib/ink-utils';
import inkableImg from '@/assets/learn-inkable.jpg';
import nonInkableImg from '@/assets/learn-non-inkable.jpg';
import loreImg from '@/assets/learn-lore.jpg';
import { useLearnProgress } from '@/hooks/useLearnProgress';
import { LEARN_PATHS, findNextIncompleteLesson, pathProgress, type LearnPath, type LearnLesson } from '@/data/learnPaths';
import { CardAnatomyExplorer } from '@/components/learn/CardAnatomyExplorer';
import { QuizRunner, QuizListItem } from '@/components/learn/QuizRunner';
import { TurnSimulator } from '@/components/learn/TurnSimulator';
import { BadgesPanel } from '@/components/learn/BadgesPanel';
import { LEARN_QUIZZES, getQuizForPath, type Quiz } from '@/data/learnQuizzes';


const QUIZ_OPTIONS = [
  { label: 'Rush in and overwhelm your opponent fast', result: { inks: ['Ruby', 'Amber'] as InkColor[], archetype: 'Aggro' } },
  { label: 'Control the board and win in the late game', result: { inks: ['Amethyst', 'Sapphire'] as InkColor[], archetype: 'Control' } },
  { label: 'Build momentum and outvalue over time', result: { inks: ['Emerald', 'Steel'] as InkColor[], archetype: 'Tempo' } },
];

type DeckTemplate = {
  name: string;
  inks: InkColor[];
  icon: any;
  badge: string;
  cost: string;
  winCon: string;
  level: 'Starter' | 'Intermediate' | 'Advanced';
};

const DECK_TEMPLATES: DeckTemplate[] = [
  // STARTER (10)
  { name: 'Ruby/Amber Aggro', inks: ['Ruby', 'Amber'], icon: Zap, badge: 'Aggro', cost: '$25–40', winCon: 'Rush the board with low-cost characters, quest aggressively for lore before your opponent stabilizes.', level: 'Starter' },
  { name: 'Amethyst/Steel Control', inks: ['Amethyst', 'Steel'], icon: Shield, badge: 'Control', cost: '$30–50', winCon: 'Remove threats with actions, ink efficiently, and win with powerful late-game characters.', level: 'Starter' },
  { name: 'Emerald/Sapphire Tempo', inks: ['Emerald', 'Sapphire'], icon: Target, badge: 'Tempo', cost: '$25–45', winCon: 'Bounce and delay opponents while efficiently questing with mid-range characters.', level: 'Starter' },
  { name: 'Amber/Emerald Ramp', inks: ['Amber', 'Emerald'], icon: Gem, badge: 'Ramp', cost: '$30–50', winCon: 'Accelerate your ink early to play powerful expensive characters ahead of curve.', level: 'Starter' },
  { name: 'Ruby/Steel Brawler', inks: ['Ruby', 'Steel'], icon: Sword, badge: 'Aggro', cost: '$25–40', winCon: 'Play tough characters that can both challenge and quest, overwhelming the opponent on board.', level: 'Starter' },
  { name: 'Sapphire/Amber Draw-Go', inks: ['Sapphire', 'Amber'], icon: Compass, badge: 'Control', cost: '$30–45', winCon: 'Draw extra cards each turn while healing to outlast your opponent and find your finishers.', level: 'Starter' },
  { name: 'Amethyst/Ruby Burn', inks: ['Amethyst', 'Ruby'], icon: Flame, badge: 'Aggro', cost: '$25–40', winCon: 'Combine direct damage actions with aggressive characters to close out games fast.', level: 'Starter' },
  { name: 'Emerald/Steel Midrange', inks: ['Emerald', 'Steel'], icon: Shield, badge: 'Midrange', cost: '$30–50', winCon: 'Build value with resilient characters and flexible removal, adapting to any matchup.', level: 'Starter' },
  { name: 'Sapphire/Amethyst Spells', inks: ['Sapphire', 'Amethyst'], icon: Wand2, badge: 'Control', cost: '$30–50', winCon: 'Use card draw and powerful actions to control the game, finishing with magic-heavy plays.', level: 'Starter' },
  { name: 'Ruby/Emerald Quest Rush', inks: ['Ruby', 'Emerald'], icon: Zap, badge: 'Aggro', cost: '$20–35', winCon: 'Play efficient questers and use Emerald\'s ramp to deploy threats faster than your opponent can answer.', level: 'Starter' },

  // INTERMEDIATE (10)
  { name: 'Ruby/Sapphire Midrange', inks: ['Ruby', 'Sapphire'], icon: Sword, badge: 'Midrange', cost: '$40–65', winCon: 'Flexible strategy combining removal and card draw to adapt to any matchup.', level: 'Intermediate' },
  { name: 'Amber/Amethyst Ramp', inks: ['Amber', 'Amethyst'], icon: Gem, badge: 'Ramp', cost: '$45–70', winCon: 'Accelerate ink to play expensive characters before your opponent can handle them.', level: 'Intermediate' },
  { name: 'Steel/Sapphire Fortress', inks: ['Steel', 'Sapphire'], icon: Shield, badge: 'Control', cost: '$50–75', winCon: 'Build an impenetrable board with equipment and items while drawing into your win condition.', level: 'Intermediate' },
  { name: 'Emerald/Amethyst Value', inks: ['Emerald', 'Amethyst'], icon: Brain, badge: 'Midrange', cost: '$45–70', winCon: 'Generate card advantage through abilities and trades, burying your opponent in value over time.', level: 'Intermediate' },
  { name: 'Ruby/Amethyst Tempo', inks: ['Ruby', 'Amethyst'], icon: Crosshair, badge: 'Tempo', cost: '$40–60', winCon: 'Disrupt opponents with removal while curving out efficiently with aggressive characters.', level: 'Intermediate' },
  { name: 'Amber/Steel Resilience', inks: ['Amber', 'Steel'], icon: Heart, badge: 'Midrange', cost: '$45–65', winCon: 'Play durable characters with healing support. Grind out wins by never letting your board die.', level: 'Intermediate' },
  { name: 'Sapphire/Emerald Bounce', inks: ['Sapphire', 'Emerald'], icon: Droplets, badge: 'Tempo', cost: '$40–60', winCon: 'Bounce opponent\'s characters repeatedly while building your board, creating massive tempo swings.', level: 'Intermediate' },
  { name: 'Amber/Ruby Weenie', inks: ['Amber', 'Ruby'], icon: Crown, badge: 'Aggro', cost: '$35–55', winCon: 'Flood the board with cheap characters, buff them with Amber support, and quest for lethal.', level: 'Intermediate' },
  { name: 'Steel/Emerald Toolbox', inks: ['Steel', 'Emerald'], icon: Compass, badge: 'Midrange', cost: '$45–70', winCon: 'Tutor for the right answer at the right time using Emerald\'s flexibility with Steel\'s consistency.', level: 'Intermediate' },
  { name: 'Amethyst/Sapphire Mill', inks: ['Amethyst', 'Sapphire'], icon: ScrollText, badge: 'Control', cost: '$50–75', winCon: 'Exhaust opponent\'s resources through card advantage and denial, winning by attrition.', level: 'Intermediate' },

  // ADVANCED (10)
  { name: 'Steel/Ruby Aggro Control', inks: ['Steel', 'Ruby'], icon: Swords, badge: 'Hybrid', cost: '$60–90', winCon: 'Aggressive early game that transitions into a controlling late game with equipment-powered characters.', level: 'Advanced' },
  { name: 'Emerald/Ruby Combo', inks: ['Emerald', 'Ruby'], icon: Flame, badge: 'Combo', cost: '$55–85', winCon: 'Ramp into a combo turn where you chain actions and abilities for massive lore in a single turn.', level: 'Advanced' },
  { name: 'Sapphire/Steel Lockdown', inks: ['Sapphire', 'Steel'], icon: Shield, badge: 'Prison', cost: '$65–100', winCon: 'Lock the opponent out of meaningful plays with bounce, items, and resilient blockers.', level: 'Advanced' },
  { name: 'Amber/Sapphire Turbo', inks: ['Amber', 'Sapphire'], icon: Zap, badge: 'Combo', cost: '$55–80', winCon: 'Draw your entire deck rapidly and assemble a game-ending combo before your opponent can react.', level: 'Advanced' },
  { name: 'Amethyst/Emerald Reanimator', inks: ['Amethyst', 'Emerald'], icon: Wand2, badge: 'Combo', cost: '$60–90', winCon: 'Discard powerful characters early, then bring them back with Amethyst abilities for huge tempo swings.', level: 'Advanced' },
  { name: 'Ruby/Sapphire Tempo Storm', inks: ['Ruby', 'Sapphire'], icon: Crosshair, badge: 'Tempo', cost: '$55–85', winCon: 'Chain removal and card draw to maintain perfect tempo, never letting your opponent develop a board.', level: 'Advanced' },
  { name: 'Steel/Amber Stax', inks: ['Steel', 'Amber'], icon: Shield, badge: 'Prison', cost: '$70–100', winCon: 'Slow the game to a crawl with taxing effects and healing, then win inevitably with persistent threats.', level: 'Advanced' },
  { name: 'Emerald/Sapphire OTK', inks: ['Emerald', 'Sapphire'], icon: Target, badge: 'Combo', cost: '$60–90', winCon: 'Set up a one-turn-kill by ramping into a massive board of questers that reach 20 lore in one action.', level: 'Advanced' },
  { name: 'Amethyst/Ruby Glass Cannon', inks: ['Amethyst', 'Ruby'], icon: Flame, badge: 'Combo', cost: '$50–80', winCon: 'All-in on damage and speed. Fragile but explosive — win by turn 5 or lose trying.', level: 'Advanced' },
  { name: 'Amber/Amethyst Enchanted', inks: ['Amber', 'Amethyst'], icon: Crown, badge: 'Control', cost: '$80–120', winCon: 'Build around powerful enchanted characters with premium support cards for an unstoppable late game.', level: 'Advanced' },
];

const HOW_TO_PLAY = [
  { title: 'Goal of the Game', content: 'Be the first player to reach 20 lore. You gain lore by sending your characters on quests. Each character has a lore value that tells you how much they earn when they quest.' },
  { title: 'Setting Up', content: 'Each player starts with a 60-card deck. Draw 7 cards for your starting hand. You can mulligan (put back any cards and draw that many) once. The first player skips their draw step on turn 1.' },
  { title: 'Inking', content: 'Once per turn, you may place a card face-down into your inkwell (if it\'s "inkable" — check the border). Ink is your resource to play cards. A card with a cost of 3 requires 3 ink (tap 3 cards in your inkwell).' },
  { title: 'Playing Cards', content: 'Pay a card\'s ink cost to play it. Characters enter play "exerted" (turned sideways) and can\'t do anything until your next turn. Items and locations enter play ready. Actions resolve immediately.' },
  { title: 'Questing', content: 'On your turn, exert a ready character to quest — gain lore equal to their lore value. But be careful: exerted characters are vulnerable to challenges!' },
  { title: 'Challenging', content: 'Exert one of your ready characters to challenge an opponent\'s exerted character. Both deal damage equal to their strength. If damage meets or exceeds willpower, the character is banished.' },
  { title: 'Winning', content: 'Keep questing to earn lore while managing your board. Protect your characters, challenge your opponent\'s threats, and be the first to 20 lore!' },
];

const STRATEGY_GUIDES = [
  { title: 'What Is Ink Curve?', difficulty: 'Beginner', time: '3 min', desc: 'Learn how to balance low and high cost cards for consistent plays every turn. A good deck has a bell curve: many 2-3 cost cards, some 4-5s, and a few expensive finishers.' },
  { title: 'Picking Your First Two Inks', difficulty: 'Beginner', time: '4 min', desc: 'Understand ink synergies and how different color pairs create unique strategies. Each ink has strengths: Ruby = damage, Amber = healing/lore, Sapphire = card draw.' },
  { title: 'When to Quest vs Challenge', difficulty: 'Beginner', time: '3 min', desc: 'The core decision of every turn. Quest to gain lore but leave yourself vulnerable, or challenge to control the board but fall behind on lore?' },
  { title: 'Reading the Meta', difficulty: 'Intermediate', time: '6 min', desc: 'How to analyze tournament results and adapt your deck to beat popular strategies. Learn about the metagame cycle and counter-building.' },
  { title: 'Uninkable Cards — How Many?', difficulty: 'Intermediate', time: '5 min', desc: 'The math behind uninkable cards and the right ratio. Most decks run 8-12 uninkable cards. Too many and you\'ll miss ink drops; too few and your late game suffers.' },
  { title: 'Board State Assessment', difficulty: 'Intermediate', time: '5 min', desc: 'Learn to evaluate who\'s winning at any point. Count lore rates, board presence, cards in hand, and ink advantage to make better decisions.' },
  { title: 'Building Around a Win Condition', difficulty: 'Advanced', time: '8 min', desc: 'Design your deck with a clear path to 20 lore and every card supporting that plan. Identify your win condition and cut everything that doesn\'t serve it.' },
  { title: 'Sideboarding Strategies', difficulty: 'Advanced', time: '7 min', desc: 'For competitive play: learn which cards to swap between games based on your opponent\'s strategy. Build a flexible 10-card sideboard.' },
  { title: 'Tempo & Card Advantage', difficulty: 'Advanced', time: '10 min', desc: 'Advanced concepts: trading cards efficiently, when to play for tempo vs value, and how to create card advantage through smart challenges and abilities.' },
];

const INK_GUIDE = [
  { ink: 'Amber' as InkColor, strengths: 'Healing, lore acceleration, buffing allies', playstyle: 'Supportive and resilient. Amber excels at keeping characters alive and questing efficiently.' },
  { ink: 'Amethyst' as InkColor, strengths: 'Magic, control, powerful late-game', playstyle: 'Mysterious and powerful. Amethyst specializes in board control and game-ending spells.' },
  { ink: 'Emerald' as InkColor, strengths: 'Ramp, flexibility, value generation', playstyle: 'Resourceful and adaptive. Emerald excels at generating extra ink and creating card advantage.' },
  { ink: 'Ruby' as InkColor, strengths: 'Direct damage, aggression, challenge power', playstyle: 'Aggressive and fiery. Ruby focuses on dealing damage and removing threats quickly.' },
  { ink: 'Sapphire' as InkColor, strengths: 'Card draw, bounce effects, knowledge', playstyle: 'Strategic and cerebral. Sapphire excels at drawing cards and disrupting opponent plans.' },
  { ink: 'Steel' as InkColor, strengths: 'Equipment, durability, steady value', playstyle: 'Reliable and tough. Steel characters are hard to remove and provide consistent value over time.' },
];

const DIFFICULTY_COLORS: Record<string, string> = {
  Beginner: 'bg-ink-emerald/20 text-ink-emerald',
  Intermediate: 'bg-primary/20 text-primary',
  Advanced: 'bg-ink-ruby/20 text-ink-ruby',
};

const LEVEL_COLORS: Record<string, string> = {
  Starter: 'bg-ink-emerald/20 text-ink-emerald',
  Intermediate: 'bg-primary/20 text-primary',
  Advanced: 'bg-ink-ruby/20 text-ink-ruby',
};

export function LearnTab() {
  const [quizResult, setQuizResult] = useState<{ inks: InkColor[]; archetype: string } | null>(null);
  const [expandedHowTo, setExpandedHowTo] = useState<number | null>(null);
  const [expandedInk, setExpandedInk] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'paths' | 'howto' | 'anatomy' | 'inks' | 'decks' | 'strategy' | 'quiz' | 'simulator' | 'badges'>('paths');
  const [openPath, setOpenPath] = useState<string | null>(null);
  const [activeQuiz, setActiveQuiz] = useState<Quiz | null>(null);
  const [deckLevel, setDeckLevel] = useState<'Starter' | 'Intermediate' | 'Advanced'>('Starter');
  const { isComplete, toggle, total } = useLearnProgress();

  useEffect(() => {
    const onNav = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (d?.tab === 'learn') {
        if (['paths', 'howto', 'anatomy', 'inks', 'decks', 'strategy', 'quiz', 'simulator', 'badges'].includes(d.subTab)) {
          setActiveSection(d.subTab);
        }
        if (d.subTab === 'paths' && d.anchor) {
          setOpenPath(d.anchor);
        }
        if (d.subTab === 'quiz' && d.anchor) {
          const q = getQuizForPath(d.anchor);
          if (q) setActiveQuiz(q);
        }
      }
    };
    window.addEventListener('lorescout:navigate-subtab', onNav);
    return () => window.removeEventListener('lorescout:navigate-subtab', onNav);
  }, []);

  const filteredDecks = DECK_TEMPLATES.filter(d => d.level === deckLevel);

  const nextLesson = useMemo(() => findNextIncompleteLesson(isComplete), [isComplete]);

  const goToLesson = (lesson: LearnLesson) => {
    // For lessons that live inside this Paths section, just open the path.
    if (lesson.subTab === 'paths') {
      setActiveSection('paths');
      return;
    }
    if (lesson.subTab === 'quiz') {
      const q = lesson.anchor ? getQuizForPath(lesson.anchor) : null;
      setActiveSection('quiz');
      setActiveQuiz(q ?? null);
      return;
    }
    if (lesson.subTab === 'simulator') {
      setActiveSection('simulator');
      return;
    }
    const parts = ['learn', lesson.subTab];
    if (lesson.anchor) parts.push(lesson.anchor);
    const newHash = `#${parts.join('/')}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
    setActiveSection(lesson.subTab as typeof activeSection);
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('lorescout:navigate-subtab', {
          detail: { tab: 'learn', subTab: lesson.subTab, anchor: lesson.anchor },
        }),
      );
      if (lesson.anchor) {
        setTimeout(() => {
          const el = document.getElementById(`learn-${lesson.anchor}`) || document.getElementById(lesson.anchor!);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 160);
      }
    }, 60);
  };

  const CompletionToggle = ({ lessonKey, label = 'Mark complete' }: { lessonKey: string; label?: string }) => {
    const done = isComplete(lessonKey);
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); toggle(lessonKey); }}
        aria-pressed={done}
        aria-label={done ? `Mark "${lessonKey}" as not complete` : `Mark "${lessonKey}" as complete`}
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium font-body transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${
          done ? 'bg-ink-emerald/20 text-ink-emerald' : 'bg-secondary text-muted-foreground hover:bg-muted'
        }`}
      >
        {done ? <Check className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
        {done ? 'Completed' : label}
      </button>
    );
  };

  const PathCard = ({ path }: { path: LearnPath }) => {
    const { done, total: t, pct } = pathProgress(path, isComplete);
    const Icon = path.icon;
    const open = openPath === path.slug;
    const earned = pct === 100;
    return (
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <button
          type="button"
          onClick={() => setOpenPath(open ? null : path.slug)}
          className="w-full text-left p-4 hover:bg-secondary/40 transition-colors"
          aria-expanded={open}
        >
          <div className="flex items-center gap-3">
            <span className={`flex h-10 w-10 items-center justify-center rounded-lg bg-secondary ${path.accent}`}>
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-sm font-semibold text-foreground truncate">{path.title}</h3>
                {earned && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-ink-amber/20 px-2 py-0.5 text-[10px] font-medium text-ink-amber font-body">
                    <Award className="h-3 w-3" /> {path.certificateTitle}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-[11px] text-muted-foreground font-body leading-snug truncate">{path.tagline}</p>
            </div>
            {open ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
          </div>
          <div className="mt-3">
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
              <div
                className={`h-full transition-all ${earned ? 'bg-ink-amber' : 'bg-primary'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground font-body">
              <span>{done} of {t} lessons</span>
              <span>{pct}%</span>
            </div>
          </div>
        </button>
        {open && (
          <ul className="border-t border-border divide-y divide-border animate-fade-in">
            {path.lessons.map((lesson) => {
              const done = isComplete(lesson.key);
              return (
                <li key={lesson.key} className="flex items-start gap-2 p-3">
                  <button
                    type="button"
                    onClick={() => toggle(lesson.key)}
                    aria-pressed={done}
                    aria-label={done ? `Mark "${lesson.title}" as not complete` : `Mark "${lesson.title}" as complete`}
                    className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${
                      done ? 'border-ink-emerald bg-ink-emerald/20 text-ink-emerald' : 'border-border text-transparent hover:border-primary'
                    }`}
                  >
                    <Check className="h-3 w-3" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-medium font-body ${done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{lesson.title}</p>
                    {lesson.blurb && (
                      <p className="text-[10px] text-muted-foreground font-body leading-snug mt-0.5">{lesson.blurb}</p>
                    )}
                  </div>
                  {lesson.subTab !== 'paths' && (
                    <button
                      type="button"
                      onClick={() => goToLesson(lesson)}
                      className="shrink-0 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-[10px] font-medium text-primary font-body hover:bg-primary/20 transition-colors"
                    >
                      Open <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="px-4 pb-4">
      {/* Section tabs */}
      <div className="flex gap-1 rounded-xl bg-secondary p-1 mt-3 overflow-x-auto">
        {([
          { id: 'paths', label: 'Paths' },
          { id: 'howto', label: 'How to Play' },
          { id: 'anatomy', label: 'Anatomy' },
          { id: 'simulator', label: 'Simulator' },
          { id: 'quiz', label: 'Quiz' },
          { id: 'badges', label: 'Badges' },
          { id: 'inks', label: 'Inks' },
          { id: 'decks', label: 'Decks' },
          { id: 'strategy', label: 'Strategy' },
        ] as const).map(s => (
          <button key={s.id} onClick={() => { setActiveSection(s.id); if (s.id !== 'quiz') setActiveQuiz(null); }} className={`flex-1 min-w-[68px] rounded-lg py-2 text-[11px] font-medium font-body transition-colors ${activeSection === s.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* QUIZZES */}
      {activeSection === 'quiz' && (
        <div className="mt-3 space-y-3">
          {activeQuiz ? (
            <QuizRunner quiz={activeQuiz} onBack={() => setActiveQuiz(null)} />
          ) : (
            <>
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <h2 className="font-display text-base font-semibold text-foreground">Try-It Quizzes</h2>
                </div>
                <p className="text-xs text-muted-foreground font-body leading-relaxed">
                  One quiz per learning path. Pass (≥70%) to mark the quiz lesson complete and finish the path.
                </p>
              </div>
              <div className="space-y-2">
                {LEARN_QUIZZES.map(q => (
                  <QuizListItem key={q.slug} quiz={q} onStart={() => setActiveQuiz(q)} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* CARD ANATOMY */}
      {activeSection === 'anatomy' && (
        <div className="mt-3">
          <CardAnatomyExplorer />
        </div>
      )}

      {/* BADGES */}
      {activeSection === 'badges' && (
        <div className="mt-3">
          <BadgesPanel />
        </div>
      )}

      {/* SAMPLE TURN SIMULATOR */}
      {activeSection === 'simulator' && (
        <div className="mt-3">
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="font-display text-base font-semibold text-foreground">Sample Turn Simulator</h2>
            </div>
            <p className="text-xs text-muted-foreground font-body leading-relaxed">
              Play a scripted turn end-to-end. Ink → Play → Quest → Challenge. The coach explains every step. Replay any time.
            </p>
          </div>
          <TurnSimulator />
        </div>
      )}

      {/* LEARNING PATHS */}
      {activeSection === 'paths' && (
        <div className="mt-3 space-y-3">
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <h2 className="font-display text-base font-semibold text-foreground">Learning Paths</h2>
              {total > 0 && (
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-ink-emerald/15 px-2 py-0.5 text-[10px] font-medium text-ink-emerald font-body">
                  <Check className="h-3 w-3" /> {total} done
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground font-body leading-relaxed">
              Four guided paths take you from your first turn to competitive play. Finish a path to earn its certificate.
            </p>
          </div>

          {nextLesson && (
            <button
              type="button"
              onClick={() => goToLesson(nextLesson.lesson)}
              className="w-full rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 to-transparent p-4 text-left hover:border-primary/60 transition-colors"
            >
              <p className="text-[10px] uppercase tracking-wider text-primary font-body">Continue where you left off</p>
              <p className="mt-1 text-sm font-semibold text-foreground font-body">{nextLesson.lesson.title}</p>
              <p className="text-[11px] text-muted-foreground font-body">in {nextLesson.path.title}</p>
              <span className="mt-2 inline-flex items-center gap-1 text-xs text-primary font-medium font-body">
                Resume <ArrowRight className="h-3 w-3" />
              </span>
            </button>
          )}

          <div className="space-y-2">
            {LEARN_PATHS.map((p) => <PathCard key={p.slug} path={p} />)}
          </div>
        </div>
      )}



      {/* HOW TO PLAY */}
      {activeSection === 'howto' && (
        <div id="learn-howto" className="mt-3 space-y-2 scroll-mt-24">
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <h2 className="font-display text-base font-semibold text-foreground">Learn Disney Lorcana</h2>
              {total > 0 && (
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-ink-emerald/15 px-2 py-0.5 text-[10px] font-medium text-ink-emerald font-body">
                  <Check className="h-3 w-3" /> {total} completed
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground font-body leading-relaxed">Master the basics of Lorcana in 7 simple steps. Tap each section to expand, and mark lessons complete — your progress syncs to your account.</p>
          </div>

          {/* Visual Guide — Inkable / Non-Inkable / Lore */}
          <div className="rounded-xl border border-border bg-card p-4 mb-3 scroll-mt-24" id="learn-visuals">
            <h3 className="text-sm font-medium text-foreground font-body mb-1">Spot the Difference</h3>
            <p className="text-[11px] text-muted-foreground font-body leading-relaxed mb-3">
              Two things to look for on every card: the <strong>border</strong> tells you if it can be inked, and the <strong>lore symbol</strong> shows how much lore the character earns when questing.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <figure
                id="learn-inkable"
                tabIndex={0}
                aria-labelledby="learn-inkable-caption"
                className="rounded-lg border border-primary/30 bg-secondary/40 p-1.5 scroll-mt-24 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background transition-shadow"
              >
                <img
                  src={inkableImg}
                  alt="An inkable Lorcana card. The card art is framed by a glowing silver swirling border that wraps around all four edges, signalling that this card can be placed face-down into your inkwell as a resource."
                  loading="lazy"
                  width={1024}
                  height={1536}
                  className="w-full aspect-[2/3] object-cover rounded-md"
                />
                <figcaption id="learn-inkable-caption" className="mt-1.5 text-center">
                  <span className="text-[10px] font-semibold text-primary font-body">Inkable</span>
                  <p className="text-[9px] text-muted-foreground font-body leading-snug mt-0.5">Silver swirl around the border — can be put into your inkwell.</p>
                  <div className="mt-1 flex justify-center">
                    <CompletionToggle lessonKey="howto-inkable" label="Got it" />
                  </div>
                </figcaption>
              </figure>
              <figure
                id="learn-non-inkable"
                tabIndex={0}
                aria-labelledby="learn-non-inkable-caption"
                className="rounded-lg border border-border bg-secondary/40 p-1.5 scroll-mt-24 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background transition-shadow"
              >
                <img
                  src={nonInkableImg}
                  alt="A non-inkable Lorcana card. The card art has a plain, solid coloured border with no silver swirling effect, meaning this card cannot be used as ink and can only be played by spending ink from your inkwell."
                  loading="lazy"
                  width={1024}
                  height={1536}
                  className="w-full aspect-[2/3] object-cover rounded-md"
                />
                <figcaption id="learn-non-inkable-caption" className="mt-1.5 text-center">
                  <span className="text-[10px] font-semibold text-foreground font-body">Non-Inkable</span>
                  <p className="text-[9px] text-muted-foreground font-body leading-snug mt-0.5">Plain border, no swirl — cannot be used as ink.</p>
                  <div className="mt-1 flex justify-center">
                    <CompletionToggle lessonKey="howto-non-inkable" label="Got it" />
                  </div>
                </figcaption>
              </figure>
            </div>
            <figure
              id="learn-lore"
              tabIndex={0}
              aria-labelledby="learn-lore-caption"
              className="mt-3 rounded-lg border border-border bg-secondary/40 p-2 scroll-mt-24 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background transition-shadow"
            >
              <img
                src={loreImg}
                alt="A close-up of a Lorcana character card highlighting the lore value: a gold teardrop-shaped symbol with a number inside it (here showing 2), indicating how much lore the character earns each time they are sent on a quest."
                loading="lazy"
                width={1024}
                height={1536}
                className="w-full max-h-48 object-contain rounded-md"
              />
              <figcaption id="learn-lore-caption" className="mt-1.5 text-center">
                <span className="text-[10px] font-semibold text-primary font-body">Lore Value</span>
                <p className="text-[9px] text-muted-foreground font-body leading-snug mt-0.5">The gold teardrop symbol shows how much lore a character earns each time they quest.</p>
                <div className="mt-1 flex justify-center">
                  <CompletionToggle lessonKey="howto-lore" label="Got it" />
                </div>
              </figcaption>
            </figure>
          </div>

          {HOW_TO_PLAY.map((step, i) => {
            const key = `howto-step-${i}`;
            return (
              <div key={i} className="w-full rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/30">
                <button onClick={() => setExpandedHowTo(expandedHowTo === i ? null : i)} className="w-full text-left">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary font-body">{i + 1}</span>
                      <h3 className="text-sm font-medium text-foreground font-body">{step.title}</h3>
                    </div>
                    {expandedHowTo === i ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  </div>
                  {expandedHowTo === i && (
                    <p className="mt-3 text-xs text-foreground/80 font-body leading-relaxed pl-10 animate-fade-in">{step.content}</p>
                  )}
                </button>
                <div className="mt-2 pl-10">
                  <CompletionToggle lessonKey={key} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* INKS GUIDE */}
      {activeSection === 'inks' && (
        <div className="mt-3 space-y-2">
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="h-5 w-5 text-primary" />
              <h2 className="font-display text-base font-semibold text-foreground">The Six Inks</h2>
            </div>
            <p className="text-xs text-muted-foreground font-body leading-relaxed">Every card belongs to one of six inks. Each has unique strengths and strategies.</p>
          </div>
          {INK_GUIDE.map(ink => (
            <button key={ink.ink} onClick={() => setExpandedInk(expandedInk === ink.ink ? null : ink.ink)} className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <InkDot ink={ink.ink} size="md" />
                  <h3 className="text-sm font-medium text-foreground font-body">{ink.ink}</h3>
                </div>
                {expandedInk === ink.ink ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              </div>
              {expandedInk === ink.ink && (
                <div className="mt-3 pl-8 space-y-2 animate-fade-in">
                  <div>
                    <p className="text-[10px] text-muted-foreground font-body uppercase tracking-wider mb-0.5">Strengths</p>
                    <p className="text-xs text-foreground font-body">{ink.strengths}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground font-body uppercase tracking-wider mb-0.5">Playstyle</p>
                    <p className="text-xs text-foreground/80 font-body leading-relaxed">{ink.playstyle}</p>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* DECK TEMPLATES */}
      {activeSection === 'decks' && (
        <div className="mt-3 space-y-3">
          {/* Ink Quiz */}
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="font-display text-base font-semibold text-foreground">Find Your Ink Identity</h2>
            </div>
            {!quizResult ? (
              <>
                <p className="text-sm text-muted-foreground mb-3 font-body">When you play, you prefer to…</p>
                <div className="space-y-2">
                  {QUIZ_OPTIONS.map((opt, i) => (
                    <button key={i} onClick={() => setQuizResult(opt.result)} className="w-full rounded-lg border border-border bg-card p-3 text-left text-sm text-foreground font-body hover:border-primary/40 transition-colors">
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="animate-fade-in">
                <p className="text-sm text-foreground mb-2 font-body">Your ink pair:</p>
                <div className="flex items-center gap-2 mb-2">
                  {quizResult.inks.map((ink) => (
                    <div key={ink} className="flex items-center gap-1.5 rounded-full bg-card px-3 py-1">
                      <InkDot ink={ink} size="md" />
                      <span className="text-sm font-medium font-body text-foreground">{ink}</span>
                    </div>
                  ))}
                  <span className="ml-auto rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium text-primary font-body">{quizResult.archetype}</span>
                </div>
                <button onClick={() => setQuizResult(null)} className="text-xs text-primary font-body underline">Try again</button>
              </div>
            )}
          </div>

          {/* Level filter */}
          <div className="flex gap-1 rounded-lg bg-secondary p-0.5">
            {(['Starter', 'Intermediate', 'Advanced'] as const).map(level => (
              <button
                key={level}
                onClick={() => setDeckLevel(level)}
                className={`flex-1 rounded-md py-1.5 text-[10px] font-medium font-body transition-colors ${deckLevel === level ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
              >
                {level}
              </button>
            ))}
          </div>

          <h2 className="font-display text-base font-semibold text-foreground">{deckLevel} Deck Templates</h2>
          {filteredDecks.map((deck) => {
            const Icon = deck.icon;
            return (
              <div key={deck.name} className="rounded-xl border border-border bg-card p-4 animate-fade-in">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-5 w-5 text-primary" />
                  <h3 className="font-display text-sm font-semibold text-foreground">{deck.name}</h3>
                  <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground font-body">{deck.cost}</span>
                </div>
                <div className="flex items-center gap-1.5 mb-2">
                  {deck.inks.map((ink) => <InkDot key={ink} ink={ink} />)}
                  <span className="text-xs text-primary font-medium font-body ml-1">{deck.badge}</span>
                  <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-medium font-body ${LEVEL_COLORS[deck.level]}`}>{deck.level}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed font-body">{deck.winCon}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* STRATEGY LESSONS */}
      {activeSection === 'strategy' && (
        <div className="mt-3 space-y-2">
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <ScrollText className="h-5 w-5 text-primary" />
              <h2 className="font-display text-base font-semibold text-foreground">Strategy Guides</h2>
            </div>
            <p className="text-xs text-muted-foreground font-body leading-relaxed">Level up your game with in-depth strategy lessons.</p>
          </div>
          {STRATEGY_GUIDES.map((lesson) => {
            const key = `strategy-${lesson.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
            return (
              <div key={lesson.title} className="rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/30">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-medium text-foreground font-body">{lesson.title}</h3>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium font-body ${DIFFICULTY_COLORS[lesson.difficulty]}`}>{lesson.difficulty}</span>
                </div>
                <p className="text-xs text-muted-foreground font-body leading-relaxed">{lesson.desc}</p>
                <div className="mt-2 flex items-center gap-2 text-[10px] text-muted-foreground font-body">
                  <Clock className="h-3 w-3" /> {lesson.time}
                  <span className="ml-auto"><CompletionToggle lessonKey={key} /></span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
